import { FC, HTMLAttributes, MouseEvent, useId, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'react-tooltip';
import styled, { css } from 'styled-components';

import tokenCoin from '../../assets/images/tokens/token-coin.png';

export type FpeTokenButtonProps = HTMLAttributes<HTMLDivElement> & {
    token?: number;
};

type TooltipPosition = {
    x: number;
    y: number;
};

const TOKEN_LIMIT = 99;
const TOKEN_BUTTON_HEIGHT = '4.375rem';
const TOKEN_BUTTON_VERTICAL_PADDING = '6.41px';
const TOKEN_COIN_SIZE = `calc(${TOKEN_BUTTON_HEIGHT} * 0.75)`;

const getRenderedToken = (token?: number) => {
    if (!token || token <= 0) {
        return '0';
    }

    if (token > TOKEN_LIMIT) {
        return `${TOKEN_LIMIT}+`;
    }

    return token.toString();
};

const FpeTokenButtonContainer = styled.div`
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    gap: 10px;
    box-sizing: border-box;
    height: ${TOKEN_BUTTON_HEIGHT};
    padding: ${TOKEN_BUTTON_VERTICAL_PADDING} 16px
        ${TOKEN_BUTTON_VERTICAL_PADDING} 4.88px;
    overflow: hidden;
    border-radius: 0.75rem;
    font-family: Signika-SemiBold;
    font-weight: 600;
    font-stretch: normal;
    font-style: normal;
    line-height: normal;
    letter-spacing: normal;
    text-align: center;

    ${({ theme: { credit } }) => `
        color: ${credit.color};
        background-image: linear-gradient(to bottom, ${credit.bodyBackground.gradientTop} 29%,
            ${credit.bodyBackground.gradientBottom} 107%);
    `};

    ${isMobile &&
    css`
        gap: 5px;
        padding-right: 8px;
    `}
`;

const TokenCoin = styled.img<{ isEmpty: boolean }>`
    width: ${TOKEN_COIN_SIZE};
    height: ${TOKEN_COIN_SIZE};
    object-fit: contain;

    ${({ isEmpty }) => isEmpty && 'filter: saturate(0);'}
`;

const TokenValueSection = styled.div`
    display: grid;
    place-items: center;
    align-self: stretch;
    font-size: 1.75rem;
    line-height: 1;

    &::before {
        content: '99+';
        grid-area: 1 / 1;
        visibility: hidden;
    }
`;

const TokenValue = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    grid-area: 1 / 1;
    height: 100%;
    transform: translateY(3px);
`;

const StyledTooltip = styled(Tooltip)`
    z-index: 9999;
    border-radius: 0.75rem;
    background: #fff !important;
    color: #170552 !important;
    font-family: Signika-SemiBold;
    font-weight: 600;
    text-align: center;
    white-space: pre-line;
`;

const TooltipIntro = styled.div`
    font-size: 18px;
`;

const TooltipToken = styled.div`
    font-size: 24px;
`;

export const FpeTokenButton: FC<FpeTokenButtonProps> = ({
    token,
    onMouseEnter,
    ...props
}) => {
    const { t } = useTranslation();
    const tooltipId = useId();
    const tokenCoinRef = useRef<HTMLImageElement>(null);
    const [tooltipPosition, setTooltipPosition] = useState<TooltipPosition>();
    const renderedToken = getRenderedToken(token);
    const isEmpty = renderedToken === '0';
    const hasTooltip = renderedToken === `${TOKEN_LIMIT}+` && !!token;

    const handleMouseEnter = (event: MouseEvent<HTMLDivElement>) => {
        const tokenCoinRect = tokenCoinRef.current?.getBoundingClientRect();

        if (tokenCoinRect) {
            setTooltipPosition({
                x: tokenCoinRect.left + tokenCoinRect.width / 2,
                y: tokenCoinRect.top + tokenCoinRect.height / 2,
            });
        }

        onMouseEnter?.(event);
    };

    return (
        <FpeTokenButtonContainer
            {...props}
            data-tooltip-id={hasTooltip ? tooltipId : undefined}
            aria-label={
                hasTooltip
                    ? `${t('tokenButton.youHave')} ${token} ${t(
                          'tokenButton.tokens'
                      )}`
                    : undefined
            }
            onMouseEnter={handleMouseEnter}
        >
            <TokenCoin
                ref={tokenCoinRef}
                src={tokenCoin}
                alt=""
                isEmpty={isEmpty}
            />
            <TokenValueSection>
                <TokenValue>{renderedToken}</TokenValue>
            </TokenValueSection>
            {hasTooltip && (
                <StyledTooltip
                    id={tooltipId}
                    place="right-start"
                    offset={0}
                    opacity={1}
                    position={tooltipPosition}
                    noArrow
                >
                    <TooltipIntro>{t('tokenButton.youHave')}</TooltipIntro>
                    <TooltipToken>
                        {token} {t('tokenButton.tokens')}
                    </TooltipToken>
                </StyledTooltip>
            )}
        </FpeTokenButtonContainer>
    );
};
